/**
 * Client Deposit Detail Page
 *
 * In-layout result/detail view for a global deposit (abono a cuenta).
 * Reached after creating a deposit and from the client's deposit history.
 * Offers a print action that opens the thermal receipt.
 */

import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle, DollarSign, FileText, Printer, Receipt } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface AppliedBill {
  bill_id: number
  bill_number: string | null
  bill_total: number
  amount_applied: number
  remaining_after: number
  created_at: string
}

interface ClientDepositReceiptData {
  business_name: string
  deposit_id: number
  client_name: string
  client_id: number
  deposit_amount: number
  payment_type: string
  reference: string | null
  notes: string | null
  deposit_created_at: string
  cashier_name: string
  total_debt: number
  applied_bills: AppliedBill[]
}

export function ClientDepositDetailPage() {
  const { id, depositId } = useParams<{ id: string; depositId: string }>()
  const navigate = useNavigate()
  const clientId = id && !isNaN(Number(id)) ? Number(id) : 0

  const { data: deposit, isLoading } = useQuery<ClientDepositReceiptData>({
    queryKey: ['client-deposit-receipt', clientId, depositId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/deposits/${depositId}/receipt`)
      return response.data
    },
    enabled: !!clientId && !!depositId,
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!deposit) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Receipt className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('receipts.notFound')}</p>
        <Button variant="outline" onClick={() => navigate(`/clients/${clientId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('deposits.backToClient')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate(`/clients/${clientId}`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold truncate">
            {t('deposits.depositRegistered')}
          </h1>
          <p className="text-sm text-muted-foreground truncate">{deposit.client_name}</p>
        </div>
      </div>

      {/* Amount Summary */}
      <Card className="border-green-200 dark:border-green-900/40 bg-green-50/50 dark:bg-green-950/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            <div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(deposit.deposit_amount)}
              </p>
              <p className="text-sm text-muted-foreground">{t('deposits.depositSuccess')}</p>
            </div>
          </div>
          <div className="flex justify-between items-center p-3 rounded-lg bg-background border">
            <span className="text-sm text-muted-foreground">{t('deposits.newTotalDebt')}</span>
            <span
              className={`text-lg font-bold ${deposit.total_debt > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
            >
              {formatCurrency(deposit.total_debt)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Deposit Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('deposits.depositDetail')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('deposits.paymentType')}</span>
            <span className="font-medium">{deposit.payment_type}</span>
          </div>
          {deposit.reference && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('deposits.reference')}</span>
              <span className="font-medium">{deposit.reference}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('bills.date')}</span>
            <span className="font-medium">{deposit.deposit_created_at}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('deposits.cashier')}</span>
            <span className="font-medium">{deposit.cashier_name}</span>
          </div>
          {deposit.notes && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground shrink-0">{t('deposits.notes')}</span>
              <span className="font-medium text-right">{deposit.notes}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Applied Bills */}
      {deposit.applied_bills.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              {t('deposits.billsAffected')} ({deposit.applied_bills.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {deposit.applied_bills.map((bill) => (
              <Link
                key={bill.bill_id}
                to={`/bills/${bill.bill_id}`}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{bill.bill_number || `#${bill.bill_id}`}</p>
                    <p className="text-xs text-muted-foreground">
                      Total: {formatCurrency(bill.bill_total)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-green-600 dark:text-green-400">
                    -{formatCurrency(bill.amount_applied)}
                  </p>
                  {bill.remaining_after <= 0 ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                      {t('deposits.paidInFull')}
                    </span>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {t('deposits.remainingDebt')}: {formatCurrency(bill.remaining_after)}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="space-y-3 pt-2">
        <Button
          className="h-12 w-full"
          onClick={() => navigate(`/receipts/client-deposit/${clientId}/${deposit.deposit_id}`)}
        >
          <Printer className="h-4 w-4 mr-2" />
          {t('deposits.printReceipt')}
        </Button>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            className="h-12 sm:flex-1"
            onClick={() => navigate(`/clients/${clientId}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('deposits.backToClient')}
          </Button>
          <Button
            variant="outline"
            className="h-12 sm:flex-1"
            onClick={() => navigate(`/clients/${clientId}/deposit`)}
          >
            <DollarSign className="h-4 w-4 mr-2" />
            {t('deposits.makeAnotherDeposit')}
          </Button>
        </div>
      </div>
    </div>
  )
}
